/**
 * ProposalNew — `/proposals/new` grant picker (Phase G4A CP2).
 *
 * Two paths:
 *   1. Pick from the user's saved grants (`GET /api/grants/saved`)
 *      → POST /api/proposals with `grant_id` + a derived working
 *      title ("Proposal for {grant.title}") → route to
 *      `/proposals/{id}/edit?step=1`.
 *   2. "Draft without a specific grant" → POST /api/proposals with
 *      no ids and the default "Untitled proposal" title → route.
 *
 * Free-tier hits the same 402 on `POST /api/proposals`; on that
 * error we route back to `/proposals` where the shared upgrade
 * panel renders.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Target, FileText, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import AppNav from "@/components/app/AppNav";
import { api, formatApiError } from "@/api/client";

export default function ProposalNew() {
  const navigate = useNavigate();
  const [savedGrants, setSavedGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingKey, setCreatingKey] = useState(null); // grant.id or "blank"

  const fetchSaved = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/grants/saved");
      setSavedGrants(res.data?.items || []);
    } catch (err) {
      // Non-fatal for the picker itself — the "Draft without a grant"
      // path still works even if the saved-grants API failed. Toast +
      // continue.
      toast.error(formatApiError(err));
      setSavedGrants([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSaved();
  }, [fetchSaved]);

  async function createProposal({ grant = null }) {
    const key = grant ? grant.id : "blank";
    setCreatingKey(key);
    try {
      const body = {};
      if (grant) {
        body.grant_id = grant.id;
        body.title = `Proposal for ${grant.title}`.slice(0, 200);
      }
      const res = await api.post("/proposals", body);
      const newId = res.data?.id;
      if (!newId) throw new Error("Proposal id missing from create response.");
      navigate(`/proposals/${newId}/edit?step=1`);
    } catch (err) {
      // Discriminate by `code` — either premium or module-disabled
      // routes back to /proposals where the correct inline shell
      // renders. G4B CP2 rerun P0 fix.
      const code = err?.response?.data?.detail?.code;
      if (code === "proposals_premium_only" || code === "feature_disabled") {
        navigate("/proposals");
        return;
      }
      toast.error(formatApiError(err));
    } finally {
      setCreatingKey(null);
    }
  }

  return (
    <main className="min-h-screen text-[var(--ds-color-ink)] bg-[var(--ds-color-background)] pb-20">
      <AppNav />
      <div className="mx-auto max-w-3xl px-5 md:px-8 pt-8">
        <Link
          to="/proposals"
          data-testid="proposal-new-back"
          className="inline-flex items-center gap-1.5 text-[13px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Proposals
        </Link>

        <header className="mt-6">
          <p
            className="text-[11px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            Grant Writer · New proposal
          </p>
          <h1
            className="mt-1 text-[28px] leading-tight font-semibold tracking-tight"
            style={{
              color: "var(--ds-color-ink)",
              fontFamily: "var(--ds-font-display)",
              letterSpacing: "-0.02em",
            }}
          >
            Pick a grant to draft for
          </h1>
          <p
            className="mt-2 text-[14px] leading-snug max-w-2xl"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            Start with one of your saved grants — the wizard prefills your
            working title so you can find it later. Or draft without a
            specific grant if you're exploring ideas.
          </p>
        </header>

        <section className="mt-6">
          <h2
            className="text-[11px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            Your saved grants
          </h2>
          {loading ? (
            <div
              data-testid="proposal-new-loading"
              className="mt-3 inline-flex items-center gap-2 text-[13px] text-[var(--ds-color-ink-muted)]"
            >
              <Loader2 className="w-4 h-4 animate-spin" /> Loading saved grants…
            </div>
          ) : savedGrants.length === 0 ? (
            <div
              data-testid="proposal-new-empty-saved"
              className="mt-3 rounded-[var(--ds-radius-card)] border border-dashed border-[var(--ds-color-border-strong)] p-6 text-[13px] text-[var(--ds-color-ink-muted)]"
            >
              You haven't saved any grants yet.{" "}
              <Link to="/grants" className="text-[var(--ds-color-accent)] hover:underline">
                Browse Grants
              </Link>{" "}
              to save one first, or draft without a grant below.
            </div>
          ) : (
            <ul className="mt-3 flex flex-col gap-2">
              {savedGrants.map((row) => {
                const g = row.grant;
                if (!g) return null;
                const busy = creatingKey === g.id;
                return (
                  <li key={g.id}>
                    <button
                      type="button"
                      data-testid={`proposal-new-pick-${g.id}`}
                      disabled={creatingKey !== null}
                      onClick={() => createProposal({ grant: g })}
                      className="w-full text-left rounded-[var(--ds-radius-card)] border border-[var(--ds-color-border)] bg-[var(--ds-color-surface)] p-4 flex items-center gap-3 hover:bg-[var(--ds-color-surface-muted)] transition-colors disabled:opacity-70"
                    >
                      <div
                        className="rounded-[var(--ds-radius-sm)] p-2 flex-shrink-0"
                        style={{
                          background: "var(--ds-color-accent-soft)",
                        }}
                      >
                        <Target
                          className="w-4 h-4"
                          style={{ color: "var(--ds-color-accent)" }}
                          aria-hidden="true"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p
                          className="text-[13px] font-semibold truncate"
                          style={{ color: "var(--ds-color-ink)" }}
                        >
                          {g.title}
                        </p>
                        <p
                          className="text-[11px] font-semibold uppercase tracking-[0.16em] truncate mt-0.5"
                          style={{ color: "var(--ds-color-ink-subtle)" }}
                        >
                          {g.agency}
                        </p>
                      </div>
                      {busy ? (
                        <Loader2 className="w-4 h-4 text-[var(--ds-color-ink-muted)] animate-spin" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-[var(--ds-color-ink-muted)]" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="mt-8">
          <h2
            className="text-[11px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            Or start without a grant
          </h2>
          <button
            type="button"
            data-testid="proposal-new-pick-blank"
            disabled={creatingKey !== null}
            onClick={() => createProposal({ grant: null })}
            className="mt-3 w-full text-left rounded-[var(--ds-radius-card)] border border-dashed border-[var(--ds-color-border-strong)] bg-[var(--ds-color-surface)] p-4 flex items-center gap-3 hover:bg-[var(--ds-color-surface-muted)] transition-colors disabled:opacity-70"
          >
            <div className="rounded-[var(--ds-radius-sm)] p-2 flex-shrink-0 bg-[var(--ds-color-surface-muted)] border border-[var(--ds-color-border)]">
              <FileText
                className="w-4 h-4"
                style={{ color: "var(--ds-color-ink-muted)" }}
                aria-hidden="true"
              />
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="text-[13px] font-semibold"
                style={{ color: "var(--ds-color-ink)" }}
              >
                Draft without a specific grant
              </p>
              <p
                className="text-[11px] font-semibold uppercase tracking-[0.16em] mt-0.5"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Useful for exploratory drafts. You can link a grant later
                from the wizard.
              </p>
            </div>
            {creatingKey === "blank" ? (
              <Loader2 className="w-4 h-4 text-[var(--ds-color-ink-muted)] animate-spin" />
            ) : (
              <ChevronRight className="w-4 h-4 text-[var(--ds-color-ink-muted)]" />
            )}
          </button>
        </section>
      </div>
    </main>
  );
}
